import React, { useCallback, useEffect, useState } from 'react';
import CustomerDialNumberPicker from '../CustomerDialNumberPicker/CustomerDialNumberPicker';
import {
  buildCustomerDialOptions,
  fetchActiveCustomerSims,
  fetchCustomerExtraPhones,
  isNameNA,
  placeBotCustomerCall,
  placeOpsCustomerCall,
} from '../../utils/customerVoiceCall';
import s from '../../pages/customers/Customers.module.scss';

/**
 * Same popup markup + styles as Customers page voice call modal.
 * Same APIs: /call-server/active-sims, /call-customer, voice /call.
 */
export default function CustomerVoiceCallModal({
  open,
  customer,
  onClose,
  onOpsCallSuccess,
  onBotCallSuccess,
}) {
  const [callName, setCallName] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [activeSims, setActiveSims] = useState([]);
  const [selectedSim, setSelectedSim] = useState('');
  const [loadingSims, setLoadingSims] = useState(false);
  const [dialOptions, setDialOptions] = useState([]);
  const [dialPhone, setDialPhone] = useState('');

  useEffect(() => {
    if (!open || !customer) return;

    const nameAvailable = !isNameNA(customer.name);
    setCallName(nameAvailable ? customer.name : '');
    setStatus(customer.phone ? 'idle' : 'error');
    setErrorMsg(customer.phone ? '' : 'Customer has no phone number');
    const initialOptions = buildCustomerDialOptions(customer.phone, customer.extra_phones);
    setDialOptions(initialOptions);
    setDialPhone(initialOptions[0]?.phone || customer.phone || '');

    if (!customer.phone && initialOptions.length === 0) return;

    let cancelled = false;
    if (customer.id) {
      fetchCustomerExtraPhones(customer.id).then((phones) => {
        if (cancelled) return;
        const options = buildCustomerDialOptions(customer.phone, phones);
        setDialOptions(options);
        setDialPhone((current) => {
          const currentTail = String(current || '').replace(/\D/g, '').slice(-10);
          const stillThere = options.some(
            (opt) => String(opt.phone).slice(-10) === currentTail,
          );
          return stillThere ? current : (options[0]?.phone || '');
        });
        if (options.length > 0) {
          setStatus('idle');
          setErrorMsg('');
        }
      });
    }

    setLoadingSims(true);
    fetchActiveCustomerSims()
      .then((sims) => {
        setActiveSims(sims);
        setSelectedSim(sims[0]?.sim_number || '');
      })
      .catch((err) => {
        console.error('Error fetching active customer SIMs:', err);
        setActiveSims([]);
        setSelectedSim('');
      })
      .finally(() => setLoadingSims(false));

    return () => {
      cancelled = true;
    };
  }, [open, customer]);

  const handleClose = useCallback(() => {
    setStatus('idle');
    setErrorMsg('');
    onClose?.();
  }, [onClose]);

  const handleOpsCall = async () => {
    if (!dialPhone) return;
    setStatus('calling');
    try {
      const data = await placeOpsCustomerCall({
        customer,
        callName,
        simNumber: selectedSim || null,
        dialPhone,
      });
      setStatus('success');
      onOpsCallSuccess?.(data);
      setTimeout(handleClose, 2000);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Failed to place call');
    }
  };

  const handleBotCall = async () => {
    if (!dialPhone) return;
    setStatus('calling');
    try {
      const data = await placeBotCustomerCall({ customer, callName, dialPhone });
      setStatus('success');
      onBotCallSuccess?.(data);
      setTimeout(handleClose, 2000);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Failed to trigger call');
    }
  };

  if (!open || !customer) return null;

  return (
    <div className={s.vcOverlay} onClick={handleClose}>
      <div className={s.vcModal} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={s.vcCloseBtn} onClick={handleClose}>
          <i className="fa fa-times" />
        </button>

        <div
          className={`${s.vcIconCircle} ${status === 'calling' ? s.vcCalling :
            status === 'success' ? s.vcSuccess :
              status === 'error' ? s.vcError : ''
          }`}
        >
          <i
            className={`fa ${status === 'calling' ? 'fa-spinner fa-spin' :
              status === 'success' ? 'fa-check' :
                status === 'error' ? 'fa-exclamation-triangle' :
                  'fa-phone'
            }`}
          />
        </div>

        <h3 className={s.vcTitle}>
          {status === 'calling' ? 'Placing Call...' :
            status === 'success' ? 'Call Triggered!' :
              status === 'error' ? 'Call Failed' :
                'Voice Call'}
        </h3>

        <div className={s.vcCustomerInfo}>
          <div className={s.vcPhoneRow}>
            <i className="fa fa-phone" />
            <span>{dialPhone || customer.phone || 'No phone'}</span>
          </div>
          {!isNameNA(customer.name) && (
            <div className={s.vcNameRow}>
              <i className="fa fa-user" />
              <span>{customer.name}</span>
            </div>
          )}
        </div>

        {status === 'idle' && isNameNA(customer.name) && (
          <div className={s.vcNameInputSection}>
            <p className={s.vcNameHint}>
              <i className="fa fa-info-circle" />
              No name on file. Add one to save it and greet them by name, or skip to call directly.
            </p>
            <input
              type="text"
              className={s.vcNameInput}
              placeholder="Enter customer name (optional)"
              value={callName}
              onChange={(e) => setCallName(e.target.value)}
              autoFocus
            />
          </div>
        )}

        {status === 'error' && errorMsg && (
          <p className={s.vcErrorMsg}>{errorMsg}</p>
        )}

        {status === 'success' && (
          <p className={s.vcSuccessMsg}>
            📞 Call dispatched to {!isNameNA(customer.name) ? customer.name : customer.phone}
          </p>
        )}

        {status === 'idle' && (
          <CustomerDialNumberPicker
            options={dialOptions}
            value={dialPhone}
            onChange={setDialPhone}
          />
        )}

        {status === 'idle' && (
          <div className={s.vcSimSection}>
            <div className={s.vcSimHeader}>
              <i className="fa fa-signal" />
              <span>Choose Outbound Phone (SIM):</span>
            </div>
            {loadingSims ? (
              <div className={s.vcSimLoading}>
                <i className="fa fa-spinner fa-spin" /> Checking connected phones...
              </div>
            ) : activeSims.length > 0 ? (
              <div className={s.vcSimList}>
                {activeSims.map((sim, idx) => {
                  const isSelected = selectedSim === sim.sim_number;
                  return (
                    <button
                      key={sim.sim_number}
                      type="button"
                      className={`${s.vcSimBtn} ${isSelected ? s.vcSimBtnActive : ''}`}
                      onClick={() => setSelectedSim(sim.sim_number)}
                    >
                      <span className={s.vcSimRadio}>
                        {isSelected && <span className={s.vcSimRadioDot} />}
                      </span>
                      <span className={s.vcSimLabel}>Phone {idx + 1}:</span>
                      <span className={s.vcSimNumber}>{sim.sim_number}</span>
                      <span className={s.vcSimOnlineDot} title="Online" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className={s.vcSimEmpty}>
                <i className="fa fa-exclamation-circle" />
                <span>No phone lines are currently available. Please try again later.</span>
              </div>
            )}
          </div>
        )}

        {status === 'idle' && (
          <div className={s.vcBtnRow}>
            <button type="button" className={s.vcCancelBtn} onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className={s.vcConfirmBtn}
              onClick={handleBotCall}
              disabled={!dialPhone}
              title="Trigger automated AI bot call"
            >
              <i className="fa fa-android" />
              Bot Call
            </button>
            <button
              type="button"
              className={s.vcOpsBtn}
              onClick={handleOpsCall}
              disabled={!dialPhone}
              title="Call directly via FaceTime/phonelink"
            >
              <i className="fa fa-phone" />
              Ops Call
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className={s.vcBtnRow}>
            <button type="button" className={s.vcCancelBtn} onClick={handleClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
